import OrganisationToaster from '@/components/organisation-toaster'
import TableTextPreview from '@/components/table/table-text-preview'
import { PageLoader } from '@/components/ui/loader'
import { AccessibilityProvider } from '@/providers/accessibility.provider'
import { ActiveOrgProvider } from '@/providers/active-org.provider'
import { FocusModeProvider } from '@/providers/focus-mode.provider'
import { FontProvider } from '@/providers/font-provider'
import { PWAProvider } from '@/providers/pwa-provider'
import { ThemeProvider } from '@/providers/theme-provider'
import { validateEnvironmentOrThrow } from '@/utils/env-validation'
import { initSentry } from '@/utils/sentry'
import { ClerkProvider } from '@clerk/clerk-react'
import Clarity from '@microsoft/clarity'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import '@xyflow/react/dist/style.css'
import 'driver.js/dist/driver.css'
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import App from './App'
import './components/text-editor/index.css'
import ErrorBoundary from './Error.Boundary'
import './index.css'

// Validate environment variables before starting the app
validateEnvironmentOrThrow()
initSentry()

if (import.meta.env.VITE_CLARITY_ENABLED === 'true') Clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID)

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

const queryClient = new QueryClient()
// disable to fetch in background
queryClient.setDefaultOptions({
  queries: {
    refetchOnWindowFocus: false,
    networkMode: 'always',
  },
  mutations: {
    networkMode: 'always',
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {import.meta.env.VITE_ENVIRONMENT === 'DEVELOPMENT' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
        <ThemeProvider>
          <AccessibilityProvider>
            <FontProvider>
              <PWAProvider>
                <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
                  <BrowserRouter>
                    <Suspense fallback={<PageLoader />}>
                      <ActiveOrgProvider>
                        <FocusModeProvider>
                          <App />
                        </FocusModeProvider>
                        <OrganisationToaster />
                        <TableTextPreview />
                      </ActiveOrgProvider>
                    </Suspense>
                  </BrowserRouter>
                </ClerkProvider>
              </PWAProvider>
            </FontProvider>
          </AccessibilityProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
)
